import Search from 'Components/Common/Search/Search';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import { IDatePicker } from 'Types/Common';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import useSuggestion from 'Hooks/useSuggestion';
import DateSearch from 'Components/Common/Search/DateSearch';
import DropdownMenuEC from 'Components/Common/EC/DropdownMenuEC';
import { ECApis } from 'Datasource/EC';
import SpinnerComponent from 'Components/Common/SpinnerComponent';
import useMessage from 'Hooks/useMessage';
interface SearchRevenueProps {
  onExportExcel?: () => void;
  isMonth?: boolean;
  loadingExcel?: boolean;
}

interface Options {
  yearMonth: string;
  disabled?: boolean;
}

const currentYear = dayjs().year();

const getCurrentMonth = () => {
  const currentMonth = dayjs().month() + 1;
  if (currentMonth < 10) {
    return `0${currentMonth}`;
  } else {
    return `${currentMonth}`;
  }
};

export default function SearchRevenue({
  onExportExcel,
  isMonth,
  loadingExcel,
}: SearchRevenueProps): JSX.Element {
  const [groupOptions, setGroupOptions] = useState<Array<Options>>([
    { yearMonth: `${currentYear}年${getCurrentMonth()}月` },
  ]);
  const [activeOption, setActiveOption] = useState(groupOptions[0]?.yearMonth);
  const { openMessageError } = useMessage();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    queryArtist: StringParam,
    queryTitle: StringParam,
    artistIds: StringParam,
    startDate: StringParam,
    endDate: StringParam,
    isOption: NumberParam,
    queryVariation: StringParam,
    queryCompany: StringParam,
    companyId: StringParam,
    variationIds: StringParam,
    titleIds: StringParam,
    companyIds: StringParam,
    artistId: StringParam,
    titleId: StringParam,
    variationId: StringParam,
  });

  const {
    companyOptions,
    artistOptions,
    titleOptions,
    variationOptions,
    loadingArtist,
    loadingTitle,
    loadingVariation,
    loadingCompany,
    handleLoadMore,
    handleFocusInput,
    handleEnterPrimary,
    handleClickItemPrimary,
  } = useSuggestion({
    fetchList: ['company', 'artist', 'title', 'variation'],
    showDraftVariation: true,
  });

  const onChangeDate = (date: any, type: string) => {
    if (!date) {
      setQuery({ [type]: '', page: 1 });
      return;
    }
    setQuery({ [type]: dayjs(date), page: 1 });
  };

  const fetchPurchaseSummaryDate = async () => {
    try {
      setIsLoading(true);
      const res = await ECApis.getPaymentSummaryDate();
      const { data } = res.data;
      const formatData = data.map((item: any) => {
        const normalDate = item.yearMonth.replace('年', '-').replace('月', '');
        const formatDate = dayjs(normalDate).format('YYYY年MM月');
        return { yearMonth: formatDate };
      });
      setGroupOptions(formatData);
    } catch (error) {
      openMessageError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickPrevMonth = () => {
    groupOptions.map((option, index) => {
      if (activeOption === option.yearMonth && index < groupOptions.length - 1) {
        setActiveOption(groupOptions[index + 1].yearMonth);
      }
    });
  };

  const handleClickNextMonth = () => {
    groupOptions.map((option, index) => {
      if (activeOption === option.yearMonth && index > 0) {
        setActiveOption(groupOptions[index - 1].yearMonth);
      }
    });
  };

  useEffect(() => {
    fetchPurchaseSummaryDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const date = activeOption.split('年');
    const year = parseInt(date[0], 10);
    const month = parseInt(date[1].substring(0, 2), 10);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    setQuery({
      startDate: startDate.toString(),
      endDate: endDate.toString(),
    });
  }, [activeOption, setQuery]);

  return (
    <>
      <Row className="align-items-end px-0">
        <SpinnerComponent isLoading={isLoading} />
        <Col md="10">
          <Row>
            <Col md="2" className="px-1 pe-3">
              <Search
                label="レコード会社"
                labelMd="12"
                colMd="12"
                placeholder="検索"
                showIcon
                classLabel="px-0"
                rowClass="w-100"
                colClass="px-0"
                searchKey="queryCompany"
                onEnter={() => handleEnterPrimary('queryCompany')}
                onClickResult={handleClickItemPrimary}
                options={companyOptions.options || []}
                loading={loadingCompany}
                onLoadMore={() => handleLoadMore('pageCompany')}
                onFocusInput={() => {
                  handleFocusInput('queryCompany', 'pageCompany');
                }}
              />
            </Col>
            <Col md="2" className="px-1 pe-3">
              <Search
                label="アーティスト"
                labelMd="12"
                colMd="12"
                placeholder="検索"
                showIcon
                classLabel="px-0"
                rowClass="w-100"
                colClass="px-0"
                searchKey="queryArtist"
                onEnter={() => handleEnterPrimary('queryArtist')}
                onClickResult={handleClickItemPrimary}
                options={artistOptions.options || []}
                loading={loadingArtist}
                onLoadMore={() => handleLoadMore('pageArtist')}
                onFocusInput={() => {
                  handleFocusInput('queryArtist', 'pageArtist');
                }}
              />
            </Col>
            <Col md="2" className="px-1 pe-3">
              <Search
                label="タイトル"
                labelMd="12"
                colMd="12"
                placeholder="検索"
                showIcon
                classLabel="px-0"
                rowClass="w-100"
                colClass="px-0"
                searchKey="queryTitle"
                onEnter={() => handleEnterPrimary('queryTitle')}
                onClickResult={handleClickItemPrimary}
                options={titleOptions.options || []}
                loading={loadingTitle}
                onLoadMore={() => handleLoadMore('pageTitle')}
                onFocusInput={() => {
                  handleFocusInput('queryTitle', 'pageTitle');
                }}
              />
            </Col>
            <Col md="2" className="px-1 pe-3">
              <Search
                label="バリエーション"
                labelMd="12"
                colMd="12"
                placeholder="検索"
                showIcon
                classLabel="px-0"
                rowClass="w-100"
                colClass="px-0"
                searchKey="queryVariation"
                onEnter={() => handleEnterPrimary('queryVariation')}
                onClickResult={handleClickItemPrimary}
                options={variationOptions.options || []}
                loading={loadingVariation}
                onLoadMore={() => handleLoadMore('pageVariation')}
                onFocusInput={() => {
                  handleFocusInput('queryVariation', 'pageVariation');
                }}
              />
            </Col>
            {isMonth ? (
              <Col md="4" className="revenue-tab px-1 pe-3">
                <div className="purchase-summary-dropdown d-flex">
                  <Button
                    onClick={handleClickPrevMonth}
                    variant="light"
                    className="purchase-summary-prev-btn"
                  >
                    前月
                  </Button>
                  <DropdownMenuEC
                    options={groupOptions}
                    activeOption={activeOption}
                    onChange={setActiveOption}
                    classButton=""
                  />
                  <Button
                    onClick={handleClickNextMonth}
                    variant="light"
                    className="purchase-summary-next-btn"
                  >
                    次月
                  </Button>
                </div>
              </Col>
            ) : (
              <>
                <Col md="2" className="px-1 pe-3">
                  <DateSearch
                    label="集計開始日"
                    labelMd="12"
                    placeholder="開始日"
                    maxDate={query.endDate ? new Date(query.endDate) : null}
                    onSelectDate={(dateStart: IDatePicker) => onChangeDate(dateStart, 'startDate')}
                    selected={query.startDate ? new Date(query.startDate) : null}
                    colMd={12}
                    searchKey="startDate"
                  />
                </Col>
                <Col md="2" className="px-1 pe-3">
                  <DateSearch
                    label="集計終了日"
                    labelMd="12"
                    placeholder="終了日"
                    minDate={query.startDate ? new Date(query.startDate) : null}
                    onSelectDate={(dateEnd: IDatePicker) => onChangeDate(dateEnd, 'endDate')}
                    selected={query.endDate ? new Date(query.endDate) : null}
                    colMd={12}
                    searchKey="endDate"
                  />
                </Col>
              </>
            )}
          </Row>
        </Col>
        <Col md="2">
          <Button
            className={`btn-focus-none prize-btn-excel float-right ${
              loadingExcel ? 'pe-none' : ''
            }`}
            onClick={onExportExcel}
          >
            {loadingExcel ? (
              <Spinner
                animation="border"
                variant="light"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              'Excel ダウンロード'
            )}
          </Button>
        </Col>
      </Row>
    </>
  );
}
